import React from 'react';
import clsx from 'clsx';
import { isEmpty, toTitleCase } from '../../../../../../../utils/utils';
import profileStyles from './UserProfile.module.scss';
import CollapsibleCard from './CollapsibleCard';
import DrawerRow from './DrawerRow';
import type { GovernmentIdsCardProps } from './ManagementUserProfile.types';

/**
 * One row per document kind, single-valued — deliberately not the multi-source shape All Known
 * Details uses. The card hides entirely when no document is held, as Director Information does;
 * once it renders, an absent kind keeps its row and shows a dash. A new document kind has to join
 * BOTH the gate below and the rows, or the card stays unreachable for a director holding only it.
 */
const GovernmentIdsCard = ({ director, expanded, onToggle }: GovernmentIdsCardProps) => {
    if (!director) return null;

    // Same emptiness rule DrawerRow dashes on, so a blank vendor string can never open a card of dashes.
    const hasPan = !isEmpty(director.pan) || !isEmpty(director.panStatus);
    const holdsAnyDocument =
        hasPan || [director.passport, director.voterId, director.drivingLicence, director.ckyc].some((value) => !isEmpty(value));
    if (!holdsAnyDocument) return null;

    return (
        <CollapsibleCard id="government-ids-panel" title="Government ID(s)" expanded={expanded} onToggle={onToggle}>
            {/* Only "Active" has a specified treatment; every other vendor string keeps the
                neutral pill rather than being given colour semantics nobody has defined for it. */}
            <DrawerRow
                label="PAN"
                value={
                    hasPan ? (
                        <>
                            {director.pan}
                            {director.panStatus && (
                                <span
                                    className={clsx(profileStyles.ValueEnd, {
                                        [profileStyles.Active]: director.panStatus.toLowerCase() === 'active',
                                        [profileStyles.Former]: director.panStatus.toLowerCase() !== 'active',
                                    })}
                                >
                                    {toTitleCase(director.panStatus)}
                                </span>
                            )}
                        </>
                    ) : undefined
                }
            />
            <DrawerRow label="Passport" value={director.passport} />
            <DrawerRow label="Voter ID" value={director.voterId} />
            <DrawerRow label="Driving License" value={director.drivingLicence} />
            <DrawerRow label="CKYC" value={director.ckyc} />
        </CollapsibleCard>
    );
};

export default GovernmentIdsCard;
